import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  COMMUNICATION_SKILLS_LESSON_NO,
  getCanDosWithTopics,
  type SkillProgressEntry,
} from "../../services/api/canDo";
import {
  COMM_CAN_DO_MASTER_THRESHOLD,
  fallbackCommTopicFromSkills,
  groupCommunicationCurriculumWithProgress,
  meanCommSkillPerformance,
  overallPctFromCommSkill,
  type CommCanDoBubbleModel,
  type CommTopicProgressGroup,
} from "../../utils/communicationCanDoProgressGrouping";
import { useStudentProgressAnalyticsData } from "../../modules/student-dashboard/hooks/useStudentProgressAnalyticsData";
import { useCommunicationStudentProgress } from "../../modules/student-dashboard/hooks/useCommunicationStudentProgress";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import { ROUTES } from "../../utils/constants";
import { BookOpen, Check, Loader2, Lock, Sparkles, Star } from "lucide-react";

/** Mean rubric ≥ this counts as "Mastered" for bubbly trail. */
const MASTER_THRESHOLD = COMM_CAN_DO_MASTER_THRESHOLD;

const NAV_GRADIENT =
  "bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 hover:brightness-110 shadow-lg shadow-purple-500/25";

/** Progress/fills aligned with app chrome (header gradient), not a separate orange accent. */
const ACCENT_GRADIENT_H = "bg-gradient-to-r from-indigo-500 via-app-primary/90 to-pink-400";
const ACCENT_GRADIENT_V = "bg-gradient-to-t from-app-primary via-app-primary/90 to-app-primary/75";

type BubbleStatus = CommCanDoBubbleModel["status"];
type CanDoBubbleModel = CommCanDoBubbleModel;
type TopicProgressGroup = CommTopicProgressGroup;

const meanPerformance = meanCommSkillPerformance;
const overallPctFromPerf = overallPctFromCommSkill;
const groupCurriculumWithProgress = groupCommunicationCurriculumWithProgress;
const fallbackTopicFromSkills = fallbackCommTopicFromSkills;

function masterySummary(bubbles: CanDoBubbleModel[]): { mastered: number; total: number } {
  const total = bubbles.length;
  const mastered = bubbles.filter((b) => b.status === "mastered").length;
  return { mastered, total };
}

/** Three thick vertical metric pills (0–100%). */
function MetricPillBars({
  fluency,
  accuracy,
  appropriateness,
  muted,
}: {
  fluency: number;
  accuracy: number;
  appropriateness: number;
  muted?: boolean;
}) {
  const { t } = useTranslation();
  const bars = [
    { short: t("gamification.commTutor.fluencyShort"), label: t("dashboard.studentHome.commCanDo.fluency"), v: Math.round(fluency * 100) },
    { short: t("gamification.commTutor.accuracyShort"), label: t("dashboard.studentHome.commCanDo.accuracy"), v: Math.round(accuracy * 100) },
    { short: t("gamification.commTutor.appropriatenessShort"), label: t("dashboard.studentHome.commCanDo.appropriateness"), v: Math.round(appropriateness * 100) },
  ];
  return (
    <div className="flex gap-2 shrink-0 items-end" aria-label={t("dashboard.studentHome.commCanDo.metricsAria")}>
      {bars.map(({ short, label, v }) => (
        <div key={label} className="flex flex-col items-center gap-1" title={`${label}: ${v}%`}>
          <div
            className={`relative flex h-[52px] w-3.5 justify-center overflow-hidden rounded-full border ${
              muted
                ? "border-slate-200 bg-slate-100 dark:border-white/15 dark:bg-white/5"
                : "border-purple-300 bg-white dark:border-purple-400/35 dark:bg-white/10"
            }`}
          >
            <div
              className={`absolute bottom-0 left-0 right-0 rounded-full transition-all duration-500 ${
                muted ? "bg-slate-300/80 dark:bg-white/15" : ACCENT_GRADIENT_V
              }`}
              style={{ height: `${muted ? 8 : Math.max(8, v)}%` }}
            />
          </div>
          <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-neutral-500">
            {short}
          </span>
        </div>
      ))}
    </div>
  );
}

function CanDoBubbleCard({ bubble }: { bubble: CanDoBubbleModel }) {
  const { t } = useTranslation();
  const { status, statement, fluency, accuracy, appropriateness, overallPct } = bubble;

  const baseLift =
    "transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl snap-start shrink-0";

  if (status === "locked") {
    return (
      <div
        className={`${baseLift} flex min-w-[min(100%,280px)] max-w-[280px] flex-col gap-3 rounded-[1.75rem] border border-slate-200/90 bg-white/90 p-4 shadow-md shadow-slate-200/60 backdrop-blur-md dark:border-white/10 dark:bg-white/[0.06] dark:shadow-lg dark:shadow-black/20`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:bg-white/10 dark:text-neutral-400">
              <Lock className="h-3 w-3" aria-hidden />
              {t("dashboard.studentHome.commCanDo.locked")}
            </span>
            <p className="text-sm font-semibold leading-snug text-slate-800 line-clamp-3 dark:text-neutral-300">
              {statement}
            </p>
          </div>
          <MetricPillBars fluency={fluency} accuracy={accuracy} appropriateness={appropriateness} muted />
        </div>
        <p className="text-[11px] text-slate-500 dark:text-neutral-500">
          {t("dashboard.studentHome.commCanDo.lockedHint")}
        </p>
      </div>
    );
  }

  if (status === "mastered") {
    return (
      <div
        className={`${baseLift} flex min-w-[min(100%,280px)] max-w-[280px] flex-col gap-3 rounded-[1.75rem] border-2 border-purple-300 bg-gradient-to-br from-purple-100/95 via-indigo-50/90 to-white p-4 shadow-lg shadow-purple-200/50 dark:border-purple-400/70 dark:from-purple-500/20 dark:via-indigo-600/15 dark:to-neutral-950 dark:shadow-[0_12px_40px_-12px_rgba(168,85,247,0.4)]`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm shadow-purple-500/30">
              <Check className="h-3.5 w-3.5 stroke-[3]" aria-hidden />
              {t("dashboard.studentHome.commCanDo.mastered")}
            </span>
            <p className="text-sm font-bold leading-snug text-slate-900 line-clamp-3 dark:text-white">{statement}</p>
          </div>
          <Star className="h-5 w-5 shrink-0 fill-pink-500 text-fuchsia-500 drop-shadow-sm dark:fill-pink-400 dark:text-fuchsia-300 dark:drop-shadow-[0_0_8px_rgba(232,121,249,0.5)]" aria-hidden />
        </div>
        <div className="flex items-end justify-between gap-2 border-t border-purple-200 pt-3 dark:border-purple-500/25">
          <MetricPillBars fluency={fluency} accuracy={accuracy} appropriateness={appropriateness} />
          <span className="text-2xl font-black tabular-nums bg-gradient-to-br from-app-primary via-app-primary/90 to-app-primary/75 bg-clip-text text-transparent dark:from-fuchsia-300 dark:via-pink-400 dark:to-pink-400">
            {overallPct}%
          </span>
        </div>
      </div>
    );
  }

  /* in_progress */
  return (
    <div
      className={`${baseLift} relative flex min-w-[min(100%,280px)] max-w-[280px] flex-col gap-3 overflow-hidden rounded-[1.75rem] border border-purple-200 bg-white p-4 shadow-md shadow-slate-200/70 dark:border-purple-500/40 dark:bg-white/[0.06] dark:shadow-lg dark:shadow-black/20`}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-[1.75rem] bg-gradient-to-br from-purple-400/[0.12] via-fuchsia-400/[0.08] to-transparent opacity-95 dark:hidden"
        style={{
          clipPath: `inset(${100 - overallPct}% 0 0 0 round 1.75rem)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-3 bottom-3 top-12 rounded-2xl bg-purple-400/10 animate-pulse dark:hidden"
        style={{ animationDuration: "2.2s" }}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="inline-flex w-fit items-center gap-1 rounded-full border border-purple-300 bg-purple-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-app-primary dark:border-purple-400/45 dark:bg-purple-500/15 dark:text-purple-200">
            <Sparkles className="h-3 w-3 text-app-primary dark:text-fuchsia-400" aria-hidden />
            {t("dashboard.studentHome.commCanDo.inProgress")}
          </span>
          <p className="text-sm font-semibold leading-snug text-slate-800 line-clamp-3 dark:text-neutral-100">{statement}</p>
        </div>
        <MetricPillBars fluency={fluency} accuracy={accuracy} appropriateness={appropriateness} />
      </div>
      <div className="relative flex items-center justify-between gap-2 border-t border-slate-200 pt-3 dark:border-white/10">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 ring-1 ring-purple-200/80 dark:bg-white/15 dark:ring-purple-400/30">
          <div
            className={`h-full rounded-full ${ACCENT_GRADIENT_H} transition-all duration-700 ease-out`}
            style={{ width: `${overallPct}%` }}
          />
        </div>
        <span className="text-lg font-black tabular-nums bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 bg-clip-text text-transparent dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300">
          {overallPct}%
        </span>
      </div>
    </div>
  );
}

/**
 * Student-only: topic-wise Communication Can-Do trail with bubbly high-contrast UI.
 * Progress from GET /v1/tutor/student-progress/me; curriculum from lesson 0 bundle + roster grade.
 */
export const CommunicationCanDoProgressCard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSchoolFeatureEnabled, loading: featureAccessLoading } = useFeatureAccess();
  const commEnabled = isSchoolFeatureEnabled("communication_tutor");
  const aiEnabled = isSchoolFeatureEnabled("ai_tutor");
  const { grade, isRosterLoading } = useStudentMe();
  const [phase, setPhase] = useState<"loading" | "forbidden" | "ready">("loading");
  const [topics, setTopics] = useState<TopicProgressGroup[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const {
    data: lessonAnalytics,
    loading: lessonAnalyticsLoading,
    error: lessonAnalyticsError,
  } = useStudentProgressAnalyticsData();
  const commProgress = useCommunicationStudentProgress({
    enabled: !featureAccessLoading && commEnabled,
  });
  const lessonLoad: "idle" | "loading" | "error" = lessonAnalyticsLoading
    ? "loading"
    : lessonAnalyticsError
      ? "error"
      : "idle";

  useEffect(() => {
    if (featureAccessLoading) return;
    if (!isSchoolFeatureEnabled("communication_tutor")) {
      setPhase("ready");
      setTopics([]);
      setSelectedTopicId(null);
      return;
    }
    if (isRosterLoading || commProgress.loading) return;
    let cancelled = false;
    void (async () => {
      if (commProgress.forbidden) {
        setPhase("forbidden");
        return;
      }
      const skills: SkillProgressEntry[] = commProgress.skills;

      let groups: TopicProgressGroup[] = [];
      try {
        if (grade != null) {
          const { canDos, communicationTopics } = await getCanDosWithTopics(
            grade,
            COMMUNICATION_SKILLS_LESSON_NO
          );
          if (cancelled) return;
          groups = groupCurriculumWithProgress(communicationTopics, canDos, skills);
        }
      } catch {
        /* roster/bundle optional */
      }

      if (cancelled) return;

      if (groups.length === 0 && skills.length > 0) {
        groups = [fallbackTopicFromSkills(skills)];
      }

      setTopics(groups);
      if (groups.length > 0) {
        const withProgress = groups.find((g) => g.bubbles.some((b) => b.status !== "locked"));
        setSelectedTopicId((withProgress ?? groups[0]).topicId);
      } else {
        setSelectedTopicId(null);
      }
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [
    grade,
    isRosterLoading,
    featureAccessLoading,
    isSchoolFeatureEnabled,
    commProgress.loading,
    commProgress.forbidden,
    commProgress.skills,
  ]);

  const activeTopic = useMemo(
    () => topics.find((t) => t.topicId === selectedTopicId) ?? topics[0] ?? null,
    [topics, selectedTopicId]
  );

  const summary = useMemo(
    () => (activeTopic ? masterySummary(activeTopic.bubbles) : { mastered: 0, total: 0 }),
    [activeTopic]
  );

  if (!featureAccessLoading && !commEnabled && !aiEnabled) return null;

  if (phase === "forbidden") return null;

  if (featureAccessLoading || (commEnabled && phase === "loading")) {
    return (
      <section className="mt-8 rounded-[1.75rem] border border-purple-200 bg-white p-6 shadow-lg shadow-purple-200/30 dark:border-purple-500/25 dark:bg-gray-800 dark:shadow-[0_20px_60px_-20px_rgba(147,51,234,0.28)]">
        <div className="flex items-center gap-3 text-slate-600 dark:text-purple-100/85">
          <Loader2 className="h-6 w-6 shrink-0 animate-spin text-app-primary dark:text-purple-400" aria-hidden />
          <span className="text-sm font-medium">{t("dashboard.studentHome.commCanDo.loading")}</span>
        </div>
      </section>
    );
  }

  const emptyNoTopics = topics.length === 0;

  return (
    <section className="mt-8 rounded-[1.75rem] border border-purple-200 bg-white p-5 shadow-xl shadow-purple-200/25 sm:p-7 dark:border-purple-500/30 dark:bg-gray-800 dark:shadow-[0_24px_64px_-24px_rgba(147,51,234,0.35)]">
      <style>{`
        .can-do-trail-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(139,92,246,0.45) rgb(241 245 249);
        }
        .dark .can-do-trail-scroll {
          scrollbar-color: rgba(167,139,250,0.55) rgba(255,255,255,0.06);
        }
        .can-do-trail-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .can-do-trail-scroll::-webkit-scrollbar-track {
          background: rgb(241 245 249);
          border-radius: 999px;
        }
        .dark .can-do-trail-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.06);
        }
        .can-do-trail-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, rgba(99,102,241,0.55), rgba(168,85,247,0.5), rgba(236,72,153,0.5));
          border-radius: 999px;
        }
        .dark .can-do-trail-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, rgba(99,102,241,0.65), rgba(168,85,247,0.55), rgba(236,72,153,0.55));
        }
      `}</style>

      {commEnabled ? (
        <>
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl dark:text-white">
            {t("dashboard.studentHome.commCanDo.title")}
          </h2>
          {activeTopic ? (
            <>
              <p className="text-lg font-bold">
                <span className="bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 bg-clip-text text-transparent dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300">
                  {t("dashboard.studentHome.commCanDo.topic")}
                </span>{" "}
                <span className="text-slate-900 dark:text-white">{activeTopic.topicTitle}</span>
              </p>
              {activeTopic.topicHint ? (
                <p className="text-sm text-slate-600 dark:text-neutral-400">{activeTopic.topicHint}</p>
              ) : null}
              <p className="text-sm font-semibold text-slate-700 dark:text-neutral-300">
                {t("dashboard.studentHome.commCanDo.masteredOf", {
                  mastered: summary.mastered,
                  total: summary.total,
                })}
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-600 dark:text-neutral-400">
              {t("dashboard.studentHome.commCanDo.emptyTopics")}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            const base = ROUTES.STUDENT_COMMUNICATION_TUTOR;
            const q =
              activeTopic && activeTopic.topicId && !activeTopic.topicId.startsWith("_")
                ? `?topic_id=${encodeURIComponent(activeTopic.topicId)}`
                : "";
            navigate(`${base}${q}`);
          }}
          className={`inline-flex shrink-0 items-center justify-center rounded-full px-6 py-3 text-sm font-bold text-white transition hover:scale-[1.02] active:scale-[0.98] ${NAV_GRADIENT}`}
        >
          {t("dashboard.studentHome.commCanDo.levelUp")}
        </button>
      </header>

      {topics.length > 1 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {topics.map((t) => {
            const { mastered, total } = masterySummary(t.bubbles);
            const active = t.topicId === selectedTopicId;
            return (
              <button
                key={t.topicId}
                type="button"
                onClick={() => setSelectedTopicId(t.topicId)}
                className={`rounded-full border px-4 py-2 text-left text-xs font-bold transition hover:-translate-y-0.5 sm:text-sm ${
                  active
                    ? "border-purple-400 bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 text-app-primary shadow-md shadow-purple-200/60 ring-1 ring-purple-300/80 dark:border-purple-400/80 dark:from-app-primary/25 dark:via-app-primary/90/25 dark:to-pink-600/20 dark:text-white dark:shadow-lg dark:shadow-indigo-500/20 dark:ring-purple-400/30"
                    : "border-slate-200 bg-slate-50 text-slate-700 hover:border-purple-300 dark:border-white/10 dark:bg-white/[0.06] dark:text-neutral-300 dark:hover:border-purple-500/40"
                }`}
              >
                {t.topicTitle}
                <span className="ml-2 tabular-nums text-slate-500 dark:text-neutral-500">
                  ({mastered}/{total})
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="mt-5">
        {emptyNoTopics ? (
          <p className="rounded-[1.5rem] border border-dashed border-purple-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-600 dark:border-purple-500/35 dark:bg-white/[0.03] dark:text-neutral-400">
            {t("dashboard.studentHome.commCanDo.unlockHint")}
          </p>
        ) : activeTopic ? (
          <>
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-neutral-500">
              {t("dashboard.studentHome.commCanDo.trailSwipe")}
            </p>
            <div className="can-do-trail-scroll -mx-1 flex gap-4 overflow-x-auto px-1 pb-3 pt-1 snap-x snap-mandatory">
              {activeTopic.bubbles.map((b) => (
                <CanDoBubbleCard key={b.can_do_id} bubble={b} />
              ))}
            </div>
          </>
        ) : null}
      </div>
        </>
      ) : null}

      {aiEnabled ? (
      <div className={`${commEnabled ? "mt-10 border-t border-purple-200/60 pt-8 dark:border-purple-500/20" : ""}`}>
        <div className="mb-4 space-y-1">
          <h3 className="text-lg font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <BookOpen className="h-5 w-5 shrink-0 text-app-primary dark:text-fuchsia-400" aria-hidden />
            {t("dashboard.studentHome.commCanDo.lessonTitle")}
          </h3>
          <p className="text-xs text-slate-600 dark:text-neutral-400 max-w-2xl">
            {t("dashboard.studentHome.commCanDo.lessonSubtitle")}
          </p>
        </div>
        {lessonLoad === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-purple-100/85">
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-app-primary dark:text-fuchsia-400" aria-hidden />
            {t("dashboard.studentHome.commCanDo.loadingLessons")}
          </div>
        ) : lessonLoad === "error" || !lessonAnalytics ? (
          <p className="rounded-[1.5rem] border border-dashed border-purple-200 bg-slate-50 px-5 py-8 text-center text-sm text-slate-600 dark:border-purple-500/35 dark:bg-white/[0.03] dark:text-neutral-400">
            {t("dashboard.studentHome.commCanDo.completeLessonHint")}
          </p>
        ) : (
          <>
            {lessonAnalytics.score_trend?.length ? (
              <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50/90 p-4 dark:border-white/10 dark:bg-white/[0.04]">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-neutral-500 mb-3">
                  {t("dashboard.studentHome.commCanDo.scoreTrend")}
                </p>
                <div className="flex h-14 items-end gap-1">
                  {lessonAnalytics.score_trend.slice(-12).map((p, i) => {
                    const v =
                      typeof p.overall_score === "number"
                        ? p.overall_score
                        : parseFloat(String(p.overall_score ?? 0));
                    const h = Number.isFinite(v) ? Math.min(100, Math.max(6, v)) : 6;
                    return (
                      <div
                        key={`${p.at ?? i}-${i}`}
                        className="min-w-[6px] flex-1 rounded-t-sm bg-gradient-to-t from-indigo-500 via-app-primary/90 to-pink-400 opacity-90"
                        style={{ height: `${h}%` }}
                        title={p.at != null ? String(p.at) : ""}
                      />
                    );
                  })}
                </div>
              </div>
            ) : null}
            {!lessonAnalytics.lessons?.length ? (
              <p className="text-sm text-slate-600 dark:text-neutral-400">
                {t("dashboard.studentHome.commCanDo.noLessonRows")}
              </p>
            ) : (
              <>
                <p className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-neutral-500">
                  {t("dashboard.studentHome.commCanDo.recentLessonsSwipe")}
                </p>
                <div className="can-do-trail-scroll -mx-1 flex gap-4 overflow-x-auto px-1 pb-3 pt-1 snap-x snap-mandatory">
                  {[...lessonAnalytics.lessons]
                    .sort((a, b) => (Number(b.lesson_no) || 0) - (Number(a.lesson_no) || 0))
                    .slice(0, 14)
                    .map((row) => {
                      const ln = Number(row.lesson_no) || 0;
                      const raw = row.overall_score;
                      const overall = typeof raw === "number" ? raw : parseFloat(String(raw ?? 0));
                      const pct = Number.isFinite(overall) ? Math.min(100, Math.max(0, overall)) : 0;
                      const mastery = row.mastery_by_can_do || {};
                      const masteredN = Object.values(mastery).filter((x) => String(x).toLowerCase() === "achieved").length;
                      const totalM = Object.keys(mastery).length;
                      return (
                        <div
                          key={ln}
                          className="snap-start min-w-[min(100%,260px)] max-w-[260px] shrink-0 rounded-[1.5rem] border border-purple-200 bg-white p-4 shadow-md shadow-slate-200/60 dark:border-purple-500/35 dark:bg-neutral-900 dark:shadow-[0_12px_36px_-14px_rgba(76,29,149,0.35)]"
                        >
                          <p className="text-xs font-bold uppercase tracking-wide text-app-primary dark:text-purple-200">
                            {t("dashboard.studentHome.lessonN", { n: ln })}
                          </p>
                          <p className="mt-2 text-2xl font-black tabular-nums bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 bg-clip-text text-transparent dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300">
                            {pct.toFixed(0)}%
                          </p>
                          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200 ring-1 ring-purple-200/80 dark:bg-white/15 dark:ring-purple-400/30">
                            <div
                              className={`h-full rounded-full ${ACCENT_GRADIENT_H} transition-all duration-700 ease-out`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="mt-2 text-xs text-slate-600 dark:text-neutral-400">
                            {t("dashboard.studentHome.commCanDo.sessions", {
                              n: row.sessions_count ?? 0,
                            })}
                            {totalM > 0 ? (
                              <>
                                {" "}
                                ·{" "}
                                {t("dashboard.studentHome.commCanDo.achievedCanDos", {
                                  mastered: masteredN,
                                  total: totalM,
                                })}
                              </>
                            ) : null}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`${ROUTES.STUDENT_TAMIL_TUTOR}?lesson_no=${encodeURIComponent(String(ln))}`)
                            }
                            className={`mt-3 w-full rounded-full py-2.5 text-xs font-bold text-white transition hover:scale-[1.02] active:scale-[0.98] ${NAV_GRADIENT}`}
                          >
                            {t("dashboard.studentHome.commCanDo.openLesson")}
                          </button>
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </>
        )}
      </div>
      ) : null}
    </section>
  );
};
